/**
 * ArkFloorPlan 布景编辑（2026-10-18）—— 只在 Admin 桌台管理（桌面、鼠标）的「布景模式」里启用。
 *
 * 进入后：
 *   - 容器加 .ark-fp-decor-edit：CSS 把桌台层整层设为不吃指针并压淡（桌台冻结，零 JS —— _applyVisualState 每次刷新都会
 *     重新开启 marker 拖动，按 marker 关拖动挡不住）；核心的 _decorEditing 标志挡掉右下画布拉伸边；
 *   - 停用 Leaflet 键盘与 Shift 框选缩放；多选由宿主先关掉（框选的 capture 监听会吞掉这里的按下）。
 * 交互：
 *   - 物件：按下即选中并拖动；区域：未选中时点一下选中、按住拖是平移画布（大区域铺满画布，不能一按就拖走），选中后才拖；
 *   - 手柄画在布景 svg 里（画布单位 + vector-effect:non-scaling-stroke），缩放手势期间随层一起变换，zoomend 后按新比例重画；
 *   - 细长物件（短边不到手柄 3 倍）只给长轴两端手柄；绿植 / 图标类锁正方形；吸附 8px，按住 Alt 不吸；
 *   - 拖动开始先落地缩放引擎（_zoomSettleNow）并静默停掉越界回弹（_zlHaltPan），拖动期间在 document capture 拦掉滚轮
 *     （否则进入 live 态、坐标变旧）；
 *   - 拖完只上报一次（OnDecorChanged）；C# 改草稿后会整份推回（setDecor），拖动中到达的推送先挂起，拖完再应用。
 * 选中：OnDecorSelected 上报；C# 可 selectDecor 指定（条目还没画出来时先挂起，整份重画后补上）。
 */

import L from 'leaflet';
import { buildDecorItem } from './ark-floor-plan-decor.js';

const SVG_NS = 'http://www.w3.org/2000/svg';
const SNAP = 8;
const MIN_SIZE = 8;
const MAX_SIZE = 4000;
const HANDLE_PX = 8;
const CLICK_SLOP = 4;
const ICON_KINDS = new Set(['restroom', 'kitchen', 'cashier', 'entrance']);
const SQUARE_KINDS = new Set(['plant', 'restroom', 'kitchen', 'cashier', 'entrance']);

function el(tag, attrs, cls) {
    const e = document.createElementNS(SVG_NS, tag);
    if (attrs) for (const k in attrs) e.setAttribute(k, String(attrs[k]));
    if (cls) e.setAttribute('class', cls);
    return e;
}

/** 条目在画布上的外框宽高（与渲染一致：图标类不转，其余 90 / 270 互换） */
function boxSize(it) {
    if (ICON_KINDS.has(it.kind)) return [it.w, it.h];
    return it.rotation === 90 || it.rotation === 270 ? [it.h, it.w] : [it.w, it.h];
}

const snap = (v, on) => (on ? Math.round(v / SNAP) * SNAP : v);

class DecorEditor {
    constructor(inst) {
        this.inst = inst;
        this.selectedId = null;
        this.pendingSelectId = null;
        this.drag = null;
        this.deferred = null;
        this.down = null;
        this._seq = 0;
    }

    enable() {
        const inst = this.inst;
        const c = inst._container;
        const map = inst.map;
        if (!c || !map) return;
        c.classList.add('ark-fp-decor-edit');
        inst._decorEditing = true;
        if (inst._resizeHover) {
            inst._resizeHover = null;
            c.style.cursor = '';
        }
        try { map.boxZoom.disable(); } catch (_) { /* 无 boxZoom 时忽略 */ }
        try { map.keyboard.disable(); } catch (_) { /* 无 keyboard 时忽略 */ }

        this._onDown = (e) => this._handleDown(e);
        c.addEventListener('mousedown', this._onDown, { capture: true });
        this._onZoomEnd = () => this._drawSelection();
        map.on('zoomend', this._onZoomEnd);
        this.onReplaced();
    }

    dispose() {
        const inst = this.inst;
        this._endDrag(false);
        this._unbindClick();
        if (inst._container) {
            inst._container.classList.remove('ark-fp-decor-edit');
            if (this._onDown) inst._container.removeEventListener('mousedown', this._onDown, { capture: true });
        }
        if (inst.map) {
            if (this._onZoomEnd) inst.map.off('zoomend', this._onZoomEnd);
            try { inst.map.boxZoom.enable(); } catch (_) { /* ignore */ }
            try { inst.map.keyboard.enable(); } catch (_) { /* ignore */ }
        }
        inst._decorEditing = false;
        const svg = inst._decorSvg;
        if (svg) {
            svg.querySelectorAll('.ark-dc-hit, .ark-dc-sel').forEach(n => n.remove());
            svg.querySelectorAll('.ark-dc-selected').forEach(n => n.classList.remove('ark-dc-selected'));
        }
        this.selectedId = null;
    }

    /** 布景整份重画后：补命中框、按 id 恢复选中（条目没了就清选中并上报） */
    onReplaced() {
        const svg = this.inst._decorSvg;
        if (!svg) return;
        svg.querySelectorAll('.ark-dc').forEach(g => this._addHit(g));
        if (this.pendingSelectId && this._findItem(this.pendingSelectId)) {
            this.selectedId = this.pendingSelectId;
            this.pendingSelectId = null;
        }
        if (this.selectedId && !this._findItem(this.selectedId)) {
            this.selectedId = null;
            this._notifySelected();
        }
        this._drawSelection();
        // 条目换了位置 / 大小（旋转、复制、撤销）→ 浮动工具栏跟上
        if (this.inst._repositionFloatingToolbar) this.inst._repositionFloatingToolbar();
    }

    /** 拖动中到达的整份推送先挂起（核心 setDecor 调） */
    deferSetDecor(items) {
        if (!this.drag) return false;
        this.deferred = items;
        return true;
    }

    /** C# 指定选中（不回报）；条目还没画出来就挂起到下一次整份重画 */
    select(id) {
        const target = id || null;
        if (target && !this._findItem(target)) {
            this.pendingSelectId = target;
            return;
        }
        this.pendingSelectId = null;
        this.selectedId = target;
        this._drawSelection();
    }

    // ---------- 内部 ----------

    _findItem(id) {
        const list = this.inst.config.decor || [];
        return list.find(it => it.id === id) || null;
    }

    _itemEl(id) {
        const svg = this.inst._decorSvg;
        if (!svg) return null;
        return Array.from(svg.querySelectorAll('.ark-dc')).find(g => g.getAttribute('data-id') === id) || null;
    }

    /** 透明命中框（固定屏幕宽度的描边扩大细长物件的可按范围），放在条目最上层 */
    _addHit(g) {
        if (g.querySelector(':scope > .ark-dc-hit')) return;
        const it = this._findItem(g.getAttribute('data-id'));
        if (!it) return;
        g.appendChild(el('rect', { x: -it.w / 2, y: -it.h / 2, width: it.w, height: it.h }, 'ark-dc-hit'));
    }

    _toCanvas(e) {
        const rect = this.inst._container.getBoundingClientRect();
        const ll = this.inst.map.containerPointToLatLng(L.point(e.clientX - rect.left, e.clientY - rect.top));
        return { x: ll.lng, y: ll.lat };
    }

    _handleDown(e) {
        if (e.button !== 0) return;
        const svg = this.inst._decorSvg;
        const t = e.target;
        const inDecor = !!(svg && t instanceof Element && svg.contains(t));

        if (inDecor) {
            const handle = t.closest('[data-handle]');
            if (handle && this.selectedId) {
                this._consume(e);
                this._startResize(e, handle.getAttribute('data-handle'));
                return;
            }
            const g = t.closest('.ark-dc');
            if (g) {
                const id = g.getAttribute('data-id');
                const kind = g.getAttribute('data-kind');
                if (kind === 'zone' && id !== this.selectedId) {
                    // 未选中的区域：不吞事件（按住拖 = 平移画布），抬起时没怎么动才算点选
                    this._armClick(e, id);
                    return;
                }
                this._consume(e);
                if (id !== this.selectedId) {
                    this.selectedId = id;
                    this._drawSelection();
                    this._notifySelected();
                }
                this._startMove(e, id);
                return;
            }
        }
        // 点空白：抬起时没怎么动 = 取消选中（拖动平移不取消）
        this._armClick(e, null);
    }

    _consume(e) {
        e.stopPropagation();
        e.preventDefault();
    }

    _armClick(e, id) {
        this._unbindClick();
        this.down = { x: e.clientX, y: e.clientY, id };
        this._onClickUp = (ev) => {
            const d = this.down;
            this._unbindClick();
            if (!d) return;
            if (Math.abs(ev.clientX - d.x) > CLICK_SLOP || Math.abs(ev.clientY - d.y) > CLICK_SLOP) return;
            if (d.id === this.selectedId) return;
            this.selectedId = d.id;
            this._drawSelection();
            this._notifySelected();
        };
        document.addEventListener('mouseup', this._onClickUp, { capture: true });
    }

    _unbindClick() {
        if (this._onClickUp) document.removeEventListener('mouseup', this._onClickUp, { capture: true });
        this._onClickUp = null;
        this.down = null;
    }

    /** 拖动起手：落地缩放引擎 + 静默停掉越界回弹（按下时核心的 map._stop() 会让回弹重新开始，回弹中画面在动、坐标会偏） */
    _settleView() {
        const inst = this.inst;
        if (inst._zoomSettleNow) inst._zoomSettleNow();
        if (inst._zlHaltPan) inst._zlHaltPan();
    }

    _startMove(e, id) {
        this._settleView();
        const item = this._findItem(id);
        if (!item) return;
        const p = this._toCanvas(e);
        this.drag = { type: 'move', item, sx: p.x, sy: p.y, cx: e.clientX, cy: e.clientY, ox: item.x, oy: item.y, moved: false };
        this._bindDrag();
    }

    _startResize(e, dir) {
        this._settleView();
        const item = this._findItem(this.selectedId);
        if (!item) return;
        const [bw, bh] = boxSize(item);
        const p = this._toCanvas(e);
        this.drag = {
            type: 'resize', dir, item, sx: p.x, sy: p.y, cx: e.clientX, cy: e.clientY, moved: false,
            l: item.x - bw / 2, t: item.y - bh / 2, r: item.x + bw / 2, b: item.y + bh / 2,
        };
        this._bindDrag();
    }

    _bindDrag() {
        if (this.inst._hideFloatingToolbar) this.inst._hideFloatingToolbar();
        this._onMove = (ev) => this._handleMove(ev);
        this._onUp = () => this._endDrag(true);
        // 拖动期间拦滚轮：缩放引擎会进入 live 态，Leaflet 坐标变旧
        this._onWheel = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
        document.addEventListener('mousemove', this._onMove);
        document.addEventListener('mouseup', this._onUp);
        document.addEventListener('wheel', this._onWheel, { capture: true, passive: false });
    }

    _handleMove(e) {
        const d = this.drag;
        if (!d) return;
        if (!d.moved && Math.abs(e.clientX - d.cx) < 3 && Math.abs(e.clientY - d.cy) < 3) return;
        d.moved = true;
        const p = this._toCanvas(e);
        const dx = p.x - d.sx;
        const dy = p.y - d.sy;
        const doSnap = !e.altKey;
        const it = d.item;

        if (d.type === 'move') {
            const [bw, bh] = boxSize(it);
            const left = snap(d.ox - bw / 2 + dx, doSnap);
            const top = snap(d.oy - bh / 2 + dy, doSnap);
            it.x = left + bw / 2;
            it.y = top + bh / 2;
        } else {
            let { l, t, r, b } = d;
            const dir = d.dir;
            if (dir.includes('w')) l = Math.min(snap(d.l + dx, doSnap), r - MIN_SIZE);
            if (dir.includes('e')) r = Math.max(snap(d.r + dx, doSnap), l + MIN_SIZE);
            if (dir.includes('n')) t = Math.min(snap(d.t + dy, doSnap), b - MIN_SIZE);
            if (dir.includes('s')) b = Math.max(snap(d.b + dy, doSnap), t + MIN_SIZE);
            if (SQUARE_KINDS.has(it.kind)) {
                // 锁正方形：取较大边，锚定对角
                const side = Math.min(MAX_SIZE, Math.max(r - l, b - t));
                if (dir.includes('w')) l = r - side; else r = l + side;
                if (dir.includes('n')) t = b - side; else b = t + side;
            }
            const bw = Math.min(MAX_SIZE, r - l);
            const bh = Math.min(MAX_SIZE, b - t);
            it.x = l + bw / 2;
            it.y = t + bh / 2;
            const swap = !ICON_KINDS.has(it.kind) && (it.rotation === 90 || it.rotation === 270);
            it.w = swap ? bh : bw;
            it.h = swap ? bw : bh;
        }
        this._rerender(it);
        this._drawSelection();
    }

    _endDrag(commit) {
        const d = this.drag;
        if (!d) return;
        document.removeEventListener('mousemove', this._onMove);
        document.removeEventListener('mouseup', this._onUp);
        document.removeEventListener('wheel', this._onWheel, { capture: true });
        this.drag = null;
        if (commit && d.moved) {
            const it = d.item;
            this.inst._notify('OnDecorChanged', { id: it.id, x: it.x, y: it.y, w: it.w, h: it.h });
        }
        if (this.deferred) {
            const items = this.deferred;
            this.deferred = null;
            this.inst.setDecor(items);
        }
        if (this.inst._repositionFloatingToolbar) this.inst._repositionFloatingToolbar();
    }

    /** 拖动中只重画这一个条目（区域素材的 pattern 随旧 <g> 一起移除） */
    _rerender(it) {
        const old = this._itemEl(it.id);
        if (!old || !old.parentNode) return;
        const fresh = buildDecorItem(it, { prefix: `${this.inst.containerId}-dce`, seq: ++this._seq });
        old.parentNode.replaceChild(fresh, old);
        this._addHit(fresh);
    }

    _notifySelected() {
        this.inst._notify('OnDecorSelected', { id: this.selectedId });
        if (this.inst._repositionFloatingToolbar) this.inst._repositionFloatingToolbar();
    }

    /** 选中框 + 手柄（画布单位；尺寸按当前缩放换算，zoomend 后重画） */
    _drawSelection() {
        const svg = this.inst._decorSvg;
        if (!svg) return;
        svg.querySelectorAll('.ark-dc-sel').forEach(n => n.remove());
        svg.querySelectorAll('.ark-dc-selected').forEach(n => n.classList.remove('ark-dc-selected'));
        const it = this.selectedId ? this._findItem(this.selectedId) : null;
        if (!it) return;
        const g = this._itemEl(it.id);
        if (g) g.classList.add('ark-dc-selected');

        const scale = Math.pow(2, this.inst.map ? this.inst.map.getZoom() : 0);
        const hs = HANDLE_PX / scale;
        const [bw, bh] = boxSize(it);
        const l = it.x - bw / 2, t = it.y - bh / 2, r = it.x + bw / 2, b = it.y + bh / 2;
        const sel = el('g', null, 'ark-dc-sel');
        sel.appendChild(el('rect', { x: l, y: t, width: bw, height: bh }, 'ark-dc-sel-box'));

        const pos = { nw: [l, t], n: [it.x, t], ne: [r, t], e: [r, it.y], se: [r, b], s: [it.x, b], sw: [l, b], w: [l, it.y] };
        let dirs;
        if (SQUARE_KINDS.has(it.kind)) dirs = ['nw', 'ne', 'se', 'sw'];
        else if (Math.min(bw, bh) * scale < HANDLE_PX * 3) dirs = bw >= bh ? ['w', 'e'] : ['n', 's'];
        else dirs = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
        for (const dir of dirs) {
            const [hx, hy] = pos[dir];
            sel.appendChild(el('rect', { x: hx - hs / 2, y: hy - hs / 2, width: hs, height: hs, 'data-handle': dir }, 'ark-dc-handle'));
        }
        svg.appendChild(sel);
    }
}

/** 开 / 关布景编辑模式 */
export function setDecorEditMode(inst, on) {
    if (!inst || !inst.map) return;
    if (on) {
        if (!inst._decorEdit) {
            inst._decorEdit = new DecorEditor(inst);
            inst._decorEdit.enable();
        }
    } else if (inst._decorEdit) {
        inst._decorEdit.dispose();
        inst._decorEdit = null;
    }
}

/** C# 指定选中的布景条目（null = 取消选中） */
export function selectDecor(inst, id) {
    if (inst && inst._decorEdit) inst._decorEdit.select(id);
}

export default { setDecorEditMode, selectDecor };
